import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/ladwjbmi_4926BBCA-F27B-484F-9467-294F35FF2347.PNG';

function RevealText({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        className={className}
        initial={{ y: '100%', opacity: 0 }}
        animate={isInView ? { y: '0%', opacity: 1 } : {}}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

/* ─── CONTACT FORM ─── */
function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', company: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <motion.div
        className="flex flex-col items-start"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="contact-success"
      >
        <div className="w-3 h-3 bg-xmx-accent mb-6" />
        <h3 className="font-cabinet text-2xl sm:text-3xl tracking-tight font-bold text-xmx-white mb-3">
          Message received.
        </h3>
        <p className="font-mono text-sm text-xmx-grey mb-8">
          We'll be in touch shortly.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="font-mono text-xs tracking-[0.2em] uppercase text-xmx-accent hover:text-xmx-accent-hover transition-colors duration-300"
          data-testid="send-another-btn"
        >
          Send another message
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
      <div className="space-y-6">
        <div className="relative">
          <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-3">
            Name
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            data-testid="contact-name"
            className="w-full bg-transparent border-b border-white/[0.1] focus:border-xmx-accent py-3 font-mono text-sm text-xmx-white outline-none transition-colors duration-300 placeholder:text-xmx-muted/40"
            placeholder="Your name"
          />
        </div>

        <div className="relative">
          <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-3">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            data-testid="contact-email"
            className="w-full bg-transparent border-b border-white/[0.1] focus:border-xmx-accent py-3 font-mono text-sm text-xmx-white outline-none transition-colors duration-300 placeholder:text-xmx-muted/40"
            placeholder="your@email.com"
          />
        </div>

        <div className="relative">
          <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-3">
            Company
          </label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            data-testid="contact-company"
            className="w-full bg-transparent border-b border-white/[0.1] focus:border-xmx-accent py-3 font-mono text-sm text-xmx-white outline-none transition-colors duration-300 placeholder:text-xmx-muted/40"
            placeholder="Company name (optional)"
          />
        </div>

        <div className="relative">
          <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-3">
            Message
          </label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            required
            rows={4}
            data-testid="contact-message"
            className="w-full bg-transparent border-b border-white/[0.1] focus:border-xmx-accent py-3 font-mono text-sm text-xmx-white outline-none transition-colors duration-300 resize-none placeholder:text-xmx-muted/40"
            placeholder="Tell us about your project"
          />
        </div>
      </div>

      <div className="pt-4">
        <button
          type="submit"
          disabled={status === 'sending'}
          data-testid="contact-submit"
          className="group relative inline-flex items-center gap-4 font-mono text-xs tracking-[0.2em] uppercase text-xmx-white hover:text-xmx-accent transition-colors duration-400 disabled:opacity-40"
        >
          <span>{status === 'sending' ? 'Sending...' : 'Send Message'}</span>
          <span className="inline-block w-8 h-[1px] bg-current group-hover:w-12 transition-all duration-300" />
        </button>

        {status === 'error' && (
          <p className="mt-4 font-mono text-xs text-red-500" data-testid="contact-error">
            Something went wrong. Please try again.
          </p>
        )}
      </div>
    </form>
  );
}

/* ─── CONTACT PAGE ─── */
export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-xmx-black">
      {/* Hero */}
      <section className="relative min-h-screen bg-xmx-black px-6 sm:px-12 overflow-hidden">
        {/* Background accent */}
        <motion.div
          className="absolute top-32 right-0 w-[1px] h-[60vh] bg-gradient-to-b from-xmx-accent/20 to-transparent hidden lg:block"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 1.5, delay: 0.5 }}
          style={{ transformOrigin: 'top' }}
        />

        <div className="max-w-6xl mx-auto pt-32 sm:pt-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12">
            {/* Left: Content */}
            <div className="lg:col-span-5 pt-8">
              <motion.span
                className="font-mono text-[10px] tracking-[0.3em] uppercase text-xmx-accent block mb-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Contact
              </motion.span>

              <motion.h1
                className="font-cabinet text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-xmx-white mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                {"Let's build"}
                <br />
                <span className="text-xmx-grey">something real.</span>
              </motion.h1>

              <motion.p
                className="font-mono text-sm leading-relaxed text-xmx-grey mb-12 max-w-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.7 }}
              >
                Whether you're exploring a new brand opportunity or need
                operational scale for an existing product — we're ready.
              </motion.p>

              {/* Contact details */}
              <motion.div
                className="space-y-8 mb-16 lg:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9 }}
              >
                <div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-2">
                    Email
                  </span>
                  <a
                    href="mailto:hello@xmxventures.com"
                    data-testid="contact-email-link"
                    className="font-mono text-sm text-xmx-grey hover:text-xmx-white transition-colors duration-300"
                  >
                    hello@xmxventures.com
                  </a>
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-2">
                    Phone
                  </span>
                  <a
                    href="tel:+16468206936"
                    data-testid="contact-phone-link"
                    className="font-mono text-sm text-xmx-grey hover:text-xmx-white transition-colors duration-300"
                  >
                    +1 (646) 820-6936
                  </a>
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-2">
                    Address
                  </span>
                  <span className="font-mono text-sm text-xmx-grey leading-relaxed block">
                    233 N Michigan Ave, Suite 2800
                    <br />
                    Chicago, IL 60601
                  </span>
                </div>
                <div>
                  <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-2">
                    Response time
                  </span>
                  <span className="font-mono text-sm text-xmx-grey">
                    Within 24 hours
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right: Form */}
            <motion.div
              className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-white/[0.06]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-xmx-black border-t border-white/[0.06] px-6 sm:px-12 py-10 mt-20" data-testid="contact-footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <img src={LOGO_URL} alt="XMX Ventures" className="h-8 w-auto opacity-40" />
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-xmx-muted">
            {new Date().getFullYear()} XMX Ventures
          </span>
        </div>
      </footer>
    </div>
  );
}
